import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useStatsigClient } from '@statsig/react-bindings';

import type {
  InstantSurvey,
  QuestionGroup,
  Question as QuestionType,
} from '@api/instant_survey/types';
import {
  QUESTION_LOGIC_OPERATORS,
  QUESTION_TYPES,
  QUESTION_TYPES_CAN_DISQUALIFY,
  QUESTION_TYPES_WITH_OPTIONS,
  QUESTION_TYPES_WITH_SUBQUESTIONS,
  SURVEY_TYPES,
} from '@api/instant_survey';
import { GATES } from '@constants';
import { getQuestionTypeIcon, getQuestionTypeName } from '@helpers';

import {
  ActionOverlayPopover,
  ArrowBendDownRightIcon,
  Card,
  CheckboxFilledIcon,
  ColumnsIcon,
  CopyIcon,
  DragDropV2Icon,
  ErrorText,
  EyeClosedIcon,
  FlowArrowIcon,
  FolderIcon,
  Layout,
  OverlayTrigger,
  PencilIcon,
  Pill,
  PinIcon,
  RadioFilledIcon,
  RowsIcon,
  sanitizeRichText,
  ShieldTipIcon,
  TargetIcon,
  Tooltip,
  TrashIcon,
} from '@utilities';

import GridAnnouncement from '../../../ModalInstantSurveyQuestion/components/GridAnnouncement';
import MaxDiffAnnouncement from '../../../ModalInstantSurveyQuestion/components/MaxDiffAnnouncement';
import { replaceScreenerPromptTokensWithValueMap } from '../../../ModalScreenerQuestion/utilities';
import ModalAnswerOptionLogic from './components/ModalAnswerOptionLogic';
import QuestionLogic from '../../../QuestionLogic';
import QuestionTypePill from '../QuestionTypePill';
import ConceptPreview from '../../../ConceptPreview';
import { useSurveyContext } from '../../../../../../utilities/context';
import {
  getNonInstructionQuestionCountBefore,
  getNumericTypeText,
  getQuestionCost,
  getQuestionLogicSourceQuestions,
  isAttentionCheckQuestion,
} from '../../../../../../utilities/helpers';

import styles from './_index.module.scss';

const answerOperators = [QUESTION_LOGIC_OPERATORS.ANY, QUESTION_LOGIC_OPERATORS.NOT];
const skipOperators = [QUESTION_LOGIC_OPERATORS.ALL, QUESTION_LOGIC_OPERATORS.ANY];

interface QuestionProps {
  canPreviewSurvey?: boolean;
  firstErrorRef: React.RefObject<HTMLDivElement | null>;
  index: number;
  isDetailedQuestionView: boolean;
  isNested?: boolean;
  isSequentialMonadicSubQuestion?: boolean;
  number: number | string;
  onDuplicateQuestion: (question: QuestionType) => void;
  onEditQuestion: (question: QuestionType) => void;
  onPreviewSurvey: (questionId: QuestionType['id']) => void;
  onRemoveFromGroup?: (question: QuestionType) => void;
  onRemoveQuestion: (question: QuestionType) => void;
  provided: any; // Provided by react-beautiful-dnd Draggable
  question: QuestionType;
  questionLogic: InstantSurvey['questionLogic'];
  selectedQuestions: QuestionType[];
  setQuestionLogic: (logic: Partial<InstantSurvey['questionLogic']>) => void;
  showActions?: boolean;
}

const Question = ({
  canPreviewSurvey,
  firstErrorRef,
  index,
  isDetailedQuestionView,
  isNested = false,
  isSequentialMonadicSubQuestion = false,
  number,
  onDuplicateQuestion,
  onEditQuestion,
  onPreviewSurvey,
  onRemoveFromGroup,
  onRemoveQuestion,
  provided,
  question,
  questionLogic,
  selectedQuestions,
  setQuestionLogic,
  showActions,
}: QuestionProps) => {
  const { client } = useStatsigClient();
  const {
    assignQuestionToGroup,
    createGroupForQuestion,
    currentQuestionCount,
    instantSurveyType,
    isReadOnly,
    maxQuestions,
    survey,
    toggleQuestionAnchored,
  } = useSurveyContext();
  const { permissions = {} } = useSelector((state: any) => state?.user?.data) || {};
  const { canAdvancedEditSurvey } = permissions;
  const isMultiLevelDisqualifyEnabled = client.checkGate(GATES.MULTI_LEVEL_DISQUALIFY);

  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [isImageEnlarged, setIsImageEnlarged] = useState(false);
  const [isOptionLogicModalOpen, setIsOptionLogicModalOpen] = useState(false);
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;
  const isCarryForwardSourceQuestion = Object.values(survey.carryForwardLogic || {}).some(
    ({ sourceSurveyQuestionId }) => sourceSurveyQuestionId === question.id
  );
  const getDisplayRichText = (text: string | undefined) =>
    question.isScreener
      ? replaceScreenerPromptTokensWithValueMap(text, question.screenerValueMap)
      : text || '';

  const addDisqualifyLogic = () => {
    const updatedLogic = { ...questionLogic };
    const disqualify = isMultiLevelDisqualifyEnabled
      ? {
          logic: [
            {
              answers: [],
              hasAnswerError: false,
              isFirstAnswerError: false,
              operator: answerOperators[0],
            },
          ],
          operator: answerOperators[0],
        }
      : {
          answers: [],
          hasAnswerError: false,
          isFirstAnswerError: false,
          operator: answerOperators[0],
        };
    updatedLogic[question.id] = {
      ...updatedLogic[question.id],
      disqualify,
    };
    setQuestionLogic(updatedLogic);
  };

  const addSkipLogic = () => {
    const updatedLogic = { ...questionLogic };
    updatedLogic[question.id] = {
      ...updatedLogic[question.id],
      skip: {
        logic: [
          {
            answers: [],
            operator: answerOperators[0],
            question: null,
          },
        ],
        operator: skipOperators[0],
      },
    };
    setQuestionLogic(updatedLogic);
  };

  const canAddDisqualifyLogic = useMemo(() => {
    return (
      QUESTION_TYPES_CAN_DISQUALIFY.includes(question.type) &&
      (canAdvancedEditSurvey || getNonInstructionQuestionCountBefore(survey.questions, index) < 2)
    );
  }, [index, question.type, survey, canAdvancedEditSurvey]);
  const showAnswerOptionLogic = client.checkGate(GATES.QUESTION_OPTION_LOGIC);
  const showQuestionGroupBlocks = client.checkGate(GATES.QUESTION_GROUP_BLOCKS);
  const canShowQuestionGroupActions = showQuestionGroupBlocks && !question.isScreener;
  const isSequentialMonadicModelMigrationEnabled = client.checkGate(
    GATES.SEQUENTIAL_MONADIC_MODEL_MIGRATION
  );
  // Skip and answer-option logic actions are only available on monadic sub-questions
  // once the model migration gate is enabled; top-level questions are unaffected.
  const canShowLogicActions =
    !isSequentialMonadicSubQuestion || isSequentialMonadicModelMigrationEnabled;

  const areAnswersDisplayed = QUESTION_TYPES_WITH_OPTIONS.includes(question.type);

  const isAttentionCheck = isAttentionCheckQuestion(question);
  const isMultiSelectQuestion = question.type === QUESTION_TYPES.CHECKBOX.value;

  const getAnswerIcon = (answer: QuestionType['answers'][number]) => {
    if (isAttentionCheck && answer.isCorrectAnswer) {
      return isMultiSelectQuestion ? <CheckboxFilledIcon /> : <RadioFilledIcon />;
    }
    return getQuestionTypeIcon(
      question.type === QUESTION_TYPES.TURF.value ? QUESTION_TYPES.CHECKBOX.value : question.type
    );
  };

  const hasBadge =
    question.showConceptImage ||
    question?.properties?.randomizeOptions ||
    question?.properties?.randomizeSubQuestions ||
    survey.carryForwardLogic?.[question.id]?.mode ||
    typeof question?.properties?.maxAnswers === 'number';

  const hasOptionLogic = [
    QUESTION_TYPES.CHECKBOX.value,
    QUESTION_TYPES.RADIO.value,
    QUESTION_TYPES.CONTSUM.value,
    QUESTION_TYPES['TABLE-RADIO'].value,
  ];
  // @ts-ignore - Question helpers are not fully typed
  const hasTooManyQuestions = currentQuestionCount + getQuestionCost(question) > maxQuestions;
  const isDuplicateDisabled = question.isScreener || hasTooManyQuestions;
  const isInstructionalText = question.type === QUESTION_TYPES.INSTRUCTIONS.value;
  const isMissingAnswers =
    QUESTION_TYPES_WITH_OPTIONS.includes(question.type) && question.answers.length === 0;
  const isMissingColumns =
    question.type === QUESTION_TYPES['TABLE-RADIO'].value && question.answers.length === 0;
  const isMissingRows =
    QUESTION_TYPES_WITH_SUBQUESTIONS.includes(question.type) && question.subQuestions.length === 0;

  const questionErrorText = (() => {
    if (isMissingAnswers) return 'Answer options are missing.';
    if (isMissingColumns) return 'Columns are missing.';
    if (isMissingRows) return 'Rows are missing.';
    return '';
  })();

  const removeQuestion = () => {
    onRemoveQuestion(question);
    setIsDropdownActive(false);
  };

  const showPreview = () => {
    onPreviewSurvey(question.id);
    setIsDropdownActive(false);
  };

  return (
    <div
      className={styles['question-draggable-container']}
      data-is-nested={isNested}
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
    >
      {questionLogic[question.id]?.skip && (
        <QuestionLogic
          firstErrorRef={firstErrorRef}
          isDetailedQuestionView={isDetailedQuestionView}
          isReadOnly={isReadOnly}
          question={question}
          questionLogic={questionLogic}
          // @ts-ignore QuestionLogic is not typed yet
          selectedQuestions={selectedQuestions}
          setQuestionLogic={setQuestionLogic}
          type="skip"
        />
      )}

      <Card
        as={Layout.Flex.Column}
        className={classNames(styles['question-card'], {
          [styles['question-card-attention-check']]: isAttentionCheck,
        })}
        elevation={1}
        gap="small"
        statusColor={question.isScreener ? 'var(--color-border-active)' : undefined}
      >
        <Layout.Flex>
          <div className={styles['question-drag-handle-wrapper']}>
            {!isReadOnly && (
              <div className={styles['question-drag-handle']}>
                <DragDropV2Icon />
              </div>
            )}
            <div className={styles['question-number']}>{number}</div>
          </div>

          <Layout.Flex.Column className={styles['question-body']}>
            <Layout.Flex gap="small">
              <div
                className={styles['question-text']}
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichText(getDisplayRichText(question.richText || question.text)),
                }}
              />
              <div className={styles['question-pill-wrapper']}>
                {question.questionGroup != null && question.anchored && (
                  <OverlayTrigger.Tooltip
                    overlay={<Tooltip>Pinned position</Tooltip>}
                    placement="top"
                  >
                    <span
                      aria-label={`Question ${number} pinned`}
                      className={styles['question-pinned-indicator']}
                      role="img"
                    >
                      <PinIcon />
                    </span>
                  </OverlayTrigger.Tooltip>
                )}
                {question.isScreener && (
                  <Pill className={styles['question-screener-pill']} isCompleted value="Screener" />
                )}
                {isAttentionCheck ? (
                  <>
                    <Pill
                      className={styles['question-attention-check-pill']}
                      icon={<ShieldTipIcon />}
                      isCompleted
                      value="Attention Check"
                    />
                  </>
                ) : (
                  <QuestionTypePill className={styles['question-type']} question={question} />
                )}
              </div>
            </Layout.Flex>

            {question.image?.cdnUrl && (
              <div className={styles['question-image-wrapper']}>
                <img
                  alt="Uploaded File"
                  className={`${styles['question-image']} ${isImageEnlarged ? styles['question-image-enlarged'] : ''}`}
                  onClick={() => setIsImageEnlarged(!isImageEnlarged)}
                  src={question.image.cdnUrl}
                />
              </div>
            )}

            {question.showConceptImage && <ConceptPreview groups={survey?.groups} />}

            {isDetailedQuestionView && (
              <div className={styles['question-detailed-view']}>
                {question.type === QUESTION_TYPES.ESSAY.value && (
                  <span className={styles['question-open-ended']}>Panelist&apos;s write-in</span>
                )}
                {question.type === QUESTION_TYPES.INSTRUCTIONS.value && (
                  <span className={styles['question-instructional-text']}>
                    This instructional text does not count as a question
                  </span>
                )}
                {question.type === QUESTION_TYPES.NUMERIC.value && (
                  <span className={styles['question-numeric-text']}>
                    Panelist&apos;s {getNumericTypeText(question.properties)}
                  </span>
                )}
                {question.type === QUESTION_TYPES.VIDEO.value && (
                  <span className={styles['question-video-response']}>
                    Panelist&apos;s video response
                  </span>
                )}
                {question.type === QUESTION_TYPES['TABLE-RADIO'].value && (
                  <Layout.Flex className={styles['question-grid']}>
                    <div className={styles['question-grid-rows']}>
                      <Pill icon={<RowsIcon />} isCompleted value="Grid Rows" />
                      <ol>
                        {question.subQuestions.map((subQuestion, index) => (
                          <li key={subQuestion.id}>
                            <strong>{index + 1}</strong>
                            <RowsIcon />
                            <div className={styles['question-answer-body']}>
                              <div className={styles['question-answer-row']}>
                                {questionLogic[question.id]?.option?.[subQuestion.id] && (
                                  <OverlayTrigger.Tooltip
                                    overlay={<Tooltip>This row has logic applied.</Tooltip>}
                                    placement="top"
                                  >
                                    <div>
                                      <FlowArrowIcon />
                                    </div>
                                  </OverlayTrigger.Tooltip>
                                )}
                                {(subQuestion.richText || subQuestion.text) && (
                                  <div
                                    className={styles['question-answer-text']}
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeRichText(
                                        getDisplayRichText(subQuestion.richText || subQuestion.text)
                                      ),
                                    }}
                                  />
                                )}
                              </div>
                              {subQuestion.image?.cdnUrl && (
                                <img
                                  alt="Uploaded File"
                                  className={styles['question-answer-image']}
                                  src={subQuestion.image.cdnUrl}
                                />
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                    <div className={styles['question-grid-columns']}>
                      <Pill icon={<ColumnsIcon />} isCompleted value="Grid Columns" />
                      <ol>
                        {question.answers.map((answer, index) => (
                          <li key={answer.id}>
                            <strong>{String.fromCharCode(65 + index)}</strong>
                            <ColumnsIcon />
                            <div
                              className={styles['question-answer-text']}
                              dangerouslySetInnerHTML={{
                                __html: sanitizeRichText(
                                  getDisplayRichText(answer.richText || answer.text)
                                ),
                              }}
                            />
                          </li>
                        ))}
                      </ol>
                    </div>
                  </Layout.Flex>
                )}

                {areAnswersDisplayed && (
                  <>
                    {question.answers
                      .slice(0, question.type === QUESTION_TYPES.MAX_DIFF.value ? 5 : undefined)
                      .filter(({ excludeFromCarryForward }) => !excludeFromCarryForward)
                      .map((answer) => (
                        <Layout.Flex
                          className={classNames(styles['question-answer'], {
                            [styles['question-answer-carry-forward']]:
                              answer.isCarryForwardGenerated,
                            [styles['question-answer-correct']]:
                              isAttentionCheck && answer.isCorrectAnswer,
                          })}
                          key={`answer-${answer.id}`}
                        >
                          <div className={styles['question-answer-icon']}>
                            {getAnswerIcon(answer)}
                          </div>

                          <Layout.Flex.Column
                            as={Layout.Fill}
                            className={styles['question-answer-body']}
                            gap="none"
                          >
                            <Layout.Flex gap="x-small">
                              {answer.isCarryForwardGenerated && (
                                <div className={styles['question-answer-carry-forward-icon']}>
                                  <ArrowBendDownRightIcon />
                                </div>
                              )}
                              {questionLogic[question.id]?.option?.[answer.id] && (
                                <OverlayTrigger.Tooltip
                                  overlay={<Tooltip>This option has logic applied.</Tooltip>}
                                  placement="top"
                                >
                                  <div>
                                    <FlowArrowIcon />
                                  </div>
                                </OverlayTrigger.Tooltip>
                              )}
                              <Layout.Fill>
                                {(answer.richText || answer.text) && (
                                  <div
                                    className={styles['question-answer-text']}
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeRichText(
                                        getDisplayRichText(answer.richText || answer.text)
                                      ),
                                    }}
                                  />
                                )}
                              </Layout.Fill>
                            </Layout.Flex>

                            {answer.image?.cdnUrl && (
                              <div>
                                <img
                                  alt="Uploaded File"
                                  className={styles['question-answer-image']}
                                  src={answer.image.cdnUrl}
                                />
                              </div>
                            )}
                          </Layout.Flex.Column>
                        </Layout.Flex>
                      ))}
                    {question.type === QUESTION_TYPES.MAX_DIFF.value &&
                      question.answers.length > 5 && (
                        <span
                          className={styles['question-more-answers']}
                        >{`or ${question.answers.length - 5} more`}</span>
                      )}
                  </>
                )}
              </div>
            )}

            {questionErrorText && (
              <ErrorText>
                {questionErrorText}
                {showActions && !isReadOnly && (
                  <button
                    className={styles['question-error-edit-link']}
                    onClick={() => onEditQuestion(question)}
                    type="button"
                  >
                    Edit
                  </button>
                )}
              </ErrorText>
            )}

            {isDetailedQuestionView && hasBadge && (
              <div className={styles['question-badges']}>
                {question.showConceptImage && <Pill isCompleted value="Concept Image Shown" />}
                {question?.properties?.maxAnswers && (
                  <Pill isCompleted value={`Max Answers: ${question.properties.maxAnswers}`} />
                )}
                {question?.properties?.randomizeOptions && (
                  <Pill isCompleted value="Randomized Order" />
                )}
                {question?.properties?.randomizeSubQuestions && (
                  <Pill isCompleted value="Randomized Question Rows" />
                )}
                {survey.carryForwardLogic?.[question.id] && (
                  <Pill
                    isCompleted
                    value={`Carry Forward: ${survey.carryForwardLogic[question.id].mode === 'SELECTED' ? 'Selected' : 'Not Selected'}`}
                  />
                )}
              </div>
            )}

            {isAttentionCheck && (
              <div className={styles['question-attention-check-note']}>
                <EyeClosedIcon />
                <span>
                  <strong>{getQuestionTypeName(question.type)} · hidden from your report.</strong>{' '}
                  Respondents who fail still complete the survey and are paid; they&apos;re
                  disqualified at completion and the released slot is automatically refilled to your
                  target N.
                </span>
              </div>
            )}
          </Layout.Flex.Column>

          {showActions && (
            <div className={styles['question-actions']}>
              <ActionOverlayPopover
                hasArrow={false}
                isActive={isDropdownActive}
                onClick={() => setIsDropdownActive(!isDropdownActive)}
                onHide={() => setIsDropdownActive(false)}
              >
                <ul>
                  {!isAttentionCheck && (
                    <li>
                      <button
                        aria-label="Edit question"
                        data-testid="edit-question-button"
                        onClick={() => {
                          onEditQuestion(question);
                          setIsDropdownActive(false);
                        }}
                      >
                        <PencilIcon />
                        {isInstructionalText ? 'Edit Text' : 'Edit Question'}
                      </button>
                    </li>
                  )}

                  {!isVideoSurvey && !isAttentionCheck && (
                    <>
                      <li>
                        <button
                          aria-label="Duplicate question"
                          data-testid="duplicate-question-button"
                          disabled={isDuplicateDisabled}
                          onClick={() => {
                            onDuplicateQuestion(question);
                            setIsDropdownActive(false);
                          }}
                        >
                          <CopyIcon />
                          {isInstructionalText ? 'Duplicate Text' : 'Duplicate Question'}
                        </button>
                      </li>

                      {canShowQuestionGroupActions && !isSequentialMonadicSubQuestion && (
                        <li>
                          <button
                            aria-label="Add to question group"
                            data-testid="add-to-question-group-button"
                            disabled={question.questionGroup != null}
                            onClick={() => {
                              createGroupForQuestion(question);
                              setIsDropdownActive(false);
                            }}
                          >
                            <FolderIcon />
                            Add to Question Group
                          </button>
                        </li>
                      )}

                      {canShowQuestionGroupActions &&
                        !isSequentialMonadicSubQuestion &&
                        onRemoveFromGroup != null && (
                          <li>
                            <button
                              aria-label="Remove from group"
                              data-testid="remove-from-group-button"
                              onClick={() => {
                                onRemoveFromGroup(question);
                                setIsDropdownActive(false);
                              }}
                            >
                              <FolderIcon />
                              Remove from Group
                            </button>
                          </li>
                        )}

                      {canShowQuestionGroupActions &&
                        !isSequentialMonadicSubQuestion &&
                        question.questionGroup != null && (
                          <li>
                            <button
                              aria-label={question.anchored ? 'Unpin question' : 'Pin question'}
                              data-testid="pin-question-button"
                              onClick={() => {
                                toggleQuestionAnchored(question);
                                setIsDropdownActive(false);
                              }}
                            >
                              <PinIcon />
                              {question.anchored ? 'Unpin' : 'Pin'} Position
                            </button>
                          </li>
                        )}

                      {canShowQuestionGroupActions &&
                        !isSequentialMonadicSubQuestion &&
                        (Object.values(survey.questionGroups || {}) as QuestionGroup[])
                          .filter((group) => group.id !== question.questionGroup)
                          .map((group) => (
                            <li key={`move-to-group-${group.id}`}>
                              <button
                                aria-label={`Move to group ${group.name}`}
                                data-testid="move-to-group-button"
                                onClick={() => {
                                  assignQuestionToGroup(question, group.id);
                                  setIsDropdownActive(false);
                                }}
                              >
                                <FolderIcon className={styles['question-move-to-group-icon']} />
                                Move to: {group.name}
                              </button>
                            </li>
                          ))}

                      {canShowLogicActions && (
                        <li>
                          <button
                            aria-label="Ask this question if"
                            data-testid="ask-this-question-if-button"
                            disabled={Boolean(questionLogic[question.id]?.skip)}
                            onClick={() => {
                              addSkipLogic();
                              setIsDropdownActive(false);
                            }}
                          >
                            <FlowArrowIcon />
                            {isInstructionalText ? 'Show This Text If' : 'Ask This Question If'}
                          </button>
                        </li>
                      )}
                      {showAnswerOptionLogic &&
                        hasOptionLogic.includes(question.type) &&
                        canShowLogicActions && (
                          <li>
                            <button
                              aria-label="Answer Options Logic"
                              data-testid="answer-options-logic-button"
                              onClick={() => {
                                setIsOptionLogicModalOpen(true);
                                setIsDropdownActive(false);
                              }}
                            >
                              <FlowArrowIcon />
                              Answer Options Logic
                            </button>
                          </li>
                        )}
                      {canAddDisqualifyLogic && !isSequentialMonadicSubQuestion && (
                        <li>
                          <button
                            aria-label="Disqualify panelists"
                            data-testid="disqualify-panelists-button"
                            disabled={Boolean(questionLogic[question.id]?.disqualify)}
                            onClick={() => {
                              addDisqualifyLogic();
                              setIsDropdownActive(false);
                            }}
                          >
                            <TargetIcon />
                            Disqualify Panelists
                          </button>
                        </li>
                      )}
                    </>
                  )}
                  {canPreviewSurvey && !isSequentialMonadicSubQuestion && (
                    <li>
                      <button
                        aria-label="Preview question"
                        data-testid="preview-question-button"
                        onClick={showPreview}
                      >
                        <span aria-hidden="true" className={styles['question-icon-slot']} />
                        Preview Question
                      </button>
                    </li>
                  )}
                  <hr />
                  <li>
                    <OverlayTrigger.Tooltip
                      overlay={
                        isCarryForwardSourceQuestion ? (
                          <Tooltip>
                            This question cannot be removed because it is used as a carry forward
                            source.
                          </Tooltip>
                        ) : (
                          <></>
                        )
                      }
                      placement="bottom"
                    >
                      <button
                        aria-label="Remove from Survey"
                        data-testid="remove-question-button"
                        disabled={isCarryForwardSourceQuestion}
                        onClick={removeQuestion}
                      >
                        <TrashIcon />
                        <span>Remove from Survey</span>
                      </button>
                    </OverlayTrigger.Tooltip>
                  </li>
                </ul>
              </ActionOverlayPopover>
            </div>
          )}
        </Layout.Flex>

        {question.type === QUESTION_TYPES['TABLE-RADIO'].value && (
          <GridAnnouncement question={question} />
        )}
        {question.type === QUESTION_TYPES.MAX_DIFF.value && (
          <MaxDiffAnnouncement question={question} />
        )}
      </Card>

      {questionLogic[question.id]?.disqualify && (
        <QuestionLogic
          firstErrorRef={firstErrorRef}
          isDetailedQuestionView={isDetailedQuestionView}
          isMultiLevelDisqualifyEnabled={isMultiLevelDisqualifyEnabled}
          isReadOnly={isReadOnly}
          question={question}
          questionLogic={questionLogic}
          setQuestionLogic={setQuestionLogic}
          type="disqualify"
        />
      )}
      {isOptionLogicModalOpen && (
        <ModalAnswerOptionLogic
          onClose={() => setIsOptionLogicModalOpen(false)}
          question={question}
          questionLogic={questionLogic}
          setQuestionLogic={setQuestionLogic}
          sourceQuestions={getQuestionLogicSourceQuestions({
            currentQuestion: question,
            questions: selectedQuestions,
          })}
        />
      )}
    </div>
  );
};

export default Question;
